/**
 * useUSNReplacement — shared hook for every reprint surface.
 *
 * Replaces the legacy "reprint" semantics: clicking "Reprint" deactivates the
 * original USN, claims a fresh USN from the REPLACE pool, and inherits all
 * properties so cross-references stay consistent.
 *
 * Returns:
 *   - replace(oldUsn, opts): single replace, returns {old_usn, new_usn} or null
 *   - replaceBatch(oldUsns, opts): bulk replace, returns full body or null
 *   - openReplaceDialog({onConfirm, count, sourceFlow}): show modal, awaits user
 *   - poolStatus: { available, used, total, section } refreshed on dialog open
 *   - submitting flag
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';

const REASON_PRESETS = ['Damaged', 'Custom (free text)'];

// Singleton state for the floating dialog (only one open at a time).
let dialogResolver = null;
let setDialogPropsExternal = null;

export function useUSNReplacement() {
  const [submitting, setSubmitting] = useState(false);
  const [poolStatus, setPoolStatus] = useState(null);

  const refreshPool = useCallback(async () => {
    try {
      const r = await apiClient.get('/usn-replacement/pool-status');
      setPoolStatus(r.data);
      return r.data;
    } catch (e) { return null; }
  }, []);

  const replace = useCallback(async (oldUsn, opts = {}) => {
    setSubmitting(true);
    try {
      const r = await apiClient.post('/usn-replacement/replace', {
        old_usn: oldUsn,
        reason: opts.reason,
        source_flow: opts.sourceFlow,
        override_dispatched: !!opts.overrideDispatched,
        linked_documents: opts.linkedDocuments || null,
      });
      return r.data;
    } catch (e) {
      // 409 ⇒ dispatched and no override. Ask for admin force inline.
      if (e.response?.status === 409) {
        const force = window.confirm(
          `${e.response.data.detail}\n\nProceed with admin override?`
        );
        if (!force) { toast.error('Cancelled'); return null; }
        const reason2 = window.prompt('Admin override reason (required, ≥3 chars):',
          (opts.reason || '') + ' [admin override]');
        if (!reason2 || reason2.trim().length < 3) { toast.error('Reason required'); return null; }
        try {
          const r2 = await apiClient.post('/usn-replacement/replace', {
            old_usn: oldUsn, reason: reason2.trim(),
            source_flow: opts.sourceFlow, override_dispatched: true,
            linked_documents: opts.linkedDocuments || null,
          });
          return r2.data;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Force replace failed');
          return null;
        }
      }
      toast.error(e.response?.data?.detail || 'Replace failed');
      return null;
    } finally { setSubmitting(false); }
  }, []);

  const replaceBatch = useCallback(async (oldUsns, opts = {}) => {
    setSubmitting(true);
    try {
      // For batches > 5, prefer the streaming endpoint so the UI can
      // surface live per-USN progress instead of staring at a spinner
      // for 30-60s. Falls back to the blocking endpoint on stream error.
      if (oldUsns.length > 5 && typeof opts.onProgress === 'function') {
        const out = await _replaceBatchStream(oldUsns, opts);
        if (out) return out;
        // _replaceBatchStream returned null → it already toasted; fall through.
      }

      // Combo session reprints can run 100+ USNs in one batch. Each USN
      // does ~40 cross-reference rewrites server-side; even with the
      // backend now fanning out via asyncio.gather we want a generous
      // client timeout so the spinner doesn't lie about the request
      // having failed when it's still in flight.
      const r = await apiClient.post(
        '/usn-replacement/replace-batch',
        {
          old_usns: oldUsns,
          reason: opts.reason,
          source_flow: opts.sourceFlow,
          override_dispatched: !!opts.overrideDispatched,
          linked_documents: opts.linkedDocuments || null,
        },
        { timeout: 180000 },  // 3 minutes
      );
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch replace failed');
      return null;
    } finally { setSubmitting(false); }
  }, []);

  /** SSE-streaming variant. Calls opts.onProgress(state) for every
   *  per-USN progress event. Returns the final body (same shape as
   *  /replace-batch) on success, or null on error. */
  async function _replaceBatchStream(oldUsns, opts) {
    const baseUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || '';
    const token = (typeof window !== 'undefined' && window.localStorage)
      ? window.localStorage.getItem('session_token') : null;
    const siteId = (typeof window !== 'undefined' && window.localStorage)
      ? window.localStorage.getItem('active_site_id') : null;
    let resp;
    try {
      resp = await fetch(`${baseUrl}/api/usn-replacement/replace-batch-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(siteId ? { 'X-Site-Id': siteId } : {}),
        },
        body: JSON.stringify({
          old_usns: oldUsns,
          reason: opts.reason,
          source_flow: opts.sourceFlow,
          override_dispatched: !!opts.overrideDispatched,
          linked_documents: opts.linkedDocuments || null,
        }),
      });
    } catch (e) {
      toast.error('Streaming connection failed; falling back to blocking call.');
      return null;
    }
    if (!resp.ok) {
      try {
        const body = await resp.json();
        toast.error(body.detail || `Stream failed (HTTP ${resp.status})`);
      } catch {
        toast.error(`Stream failed (HTTP ${resp.status})`);
      }
      return null;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let final = null;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Each event is delimited by a blank line.
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';
      for (const e of events) {
        const line = e.trim();
        if (!line.startsWith('data:')) continue;
        let parsed;
        try { parsed = JSON.parse(line.slice(5).trim()); }
        catch { continue; }
        if (parsed.type === 'progress' || parsed.type === 'start') {
          if (typeof opts.onProgress === 'function') {
            opts.onProgress(parsed);
          }
        } else if (parsed.type === 'complete') {
          final = parsed;
        } else if (parsed.type === 'error') {
          toast.error(parsed.detail || 'Stream error');
        }
      }
    }
    return final;
  }

  /** Show the reason-picker dialog and resolve with {reason} or null on cancel. */
  const openReplaceDialog = useCallback(async ({ count = 1, sourceFlow = '', subjectLabel = '' }) => {
    await refreshPool();
    return new Promise((resolve) => {
      dialogResolver = resolve;
      if (setDialogPropsExternal) {
        setDialogPropsExternal({
          open: true, count, sourceFlow, subjectLabel,
        });
      } else {
        // No mounted dialog — fall back to native prompt
        const reason = window.prompt(
          `Replace ${count} USN(s)?\n\nReason (Damaged or custom, ≥ 3 chars):`,
          'Damaged'
        );
        resolve(reason && reason.trim().length >= 3 ? { reason: reason.trim() } : null);
        dialogResolver = null;
      }
    });
  }, [refreshPool]);

  return { replace, replaceBatch, openReplaceDialog, refreshPool, poolStatus, submitting };
}

export const REPLACEMENT_REASON_PRESETS = REASON_PRESETS;

// Internal hookups for the singleton dialog
export function _registerDialogSetter(setter) {
  setDialogPropsExternal = setter;
}
export function _resolveDialog(value) {
  if (dialogResolver) {
    dialogResolver(value);
    dialogResolver = null;
  }
}
